// router.js — a hash router in 40 lines.
//
// Hash routing, not history routing, for the same reason vite.config sets
// `base: './'`: the build must drop onto any static host — GitHub Pages, a
// Railway static service, a subpath behind the API's reverse proxy — without
// server rewrite rules. A missing rewrite rule on a history router is a 404 on
// every deep link, and it is discovered by the person on-call, not in review.

import React from 'react';

/** '#/orgs/abc123?tab=users' → { path: '/orgs/abc123', parts: ['orgs','abc123'], query } */
export function parseHash(hash) {
  const raw = (hash || '').replace(/^#/, '') || '/';
  const [pathname, search] = raw.split('?');
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return {
    path,
    parts: path.split('/').filter(Boolean),
    query: Object.fromEntries(new URLSearchParams(search || '')),
  };
}

export function navigate(to, { replace = false } = {}) {
  const target = `#${to.startsWith('/') ? to : `/${to}`}`;
  if (replace) window.history.replaceState(null, '', target);
  else window.location.hash = target;
  if (replace) window.dispatchEvent(new HashChangeEvent('hashchange'));
}

export function useRoute() {
  const [route, setRoute] = React.useState(() => parseHash(window.location.hash));
  React.useEffect(() => {
    const on = () => {
      setRoute(parseHash(window.location.hash));
      // Every navigation is a new page: start at the top, as a phone user
      // expects, rather than keeping the previous screen's scroll offset.
      window.scrollTo(0, 0);
    };
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return route;
}
